import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone } from "lucide-react";
import { WhatsAppIcon } from "../components/common/WhatsAppIcon";
import { PageHero } from "../components/common/PageHero";
import { RandevuForm } from "../components/common/RandevuForm";
import { Reveal } from "../components/motion/Reveal";
import { useC, useSite, useWa } from "../context/SiteData";
import { API } from "../lib/api";
import { getMapEmbedUrl } from "../lib/maps";


const Iletisim = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const c = useC();
  const site = useSite();
  const wa = useWa();

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mesajınız alındı. En kısa sürede size dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen telefon veya WhatsApp ile deneyin.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        title={c.ph_iletisim_title}
        subtitle={c.ph_iletisim_subtitle}
        desc={c.ph_iletisim_desc}
      />
      <section data-testid="iletisim-section" className="section-y">
        <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <div>
            <Reveal>
              <div className="space-y-px border border-line bg-line">
                <div className="bg-white p-7">
                  <p className="label text-ink-400">Adres</p>
                  <p className="mt-3 flex items-start gap-3 text-base font-semibold leading-relaxed text-ink-900">
                    <MapPin size={18} className="mt-1 shrink-0 text-ink-400" />
                    {site.address}
                  </p>
                  <p className="ml-[30px] text-base font-semibold text-ink-900">{site.addressCity}</p>
                </div>
                <a href={site.phoneHref} data-testid="contact-phone" className="block bg-white p-7 transition-colors duration-300 hover:bg-cream-100">
                  <p className="label text-ink-400">Telefon</p>
                  <p className="mt-3 flex items-center gap-3 text-base font-semibold text-ink-900">
                    <Phone size={18} className="shrink-0 text-ink-400" />
                    {site.phone}
                  </p>
                </a>
                <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="block bg-white p-7 transition-colors duration-300 hover:bg-cream-100">
                  <p className="label text-ink-400">WhatsApp</p>
                  <p className="mt-3 flex items-center gap-3 text-base font-semibold text-ink-900">
                    <WhatsAppIcon size={18} className="shrink-0 text-ink-400" />
                    WhatsApp'tan Bilgi Al
                  </p>
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-6 overflow-hidden border border-line">
                <iframe
                  title="Yalı Yurt Konum"
                  src={getMapEmbedUrl(c)}
                  className="map-grayscale aspect-[16/10] w-full"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="border border-line bg-white p-7 sm:p-10">
              <h2 className="text-2xl font-bold tracking-tight text-ink-900">{c.contact_form_title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-500">
                {c.contact_form_text}
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label htmlFor="name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Adınız Soyadınız *</label>
                  <input id="name" name="name" required value={form.name} onChange={onChange} data-testid="contact-input-name" className="input-field" placeholder="Ad Soyad" />
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Telefon Numaranız *</label>
                  <input id="phone" name="phone" type="tel" required value={form.phone} onChange={onChange} data-testid="contact-input-phone" className="input-field" placeholder="05__ ___ __ __" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">E-posta Adresiniz</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={onChange} data-testid="contact-input-email" className="input-field" placeholder="ornek@eposta.com" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Mesajınız *</label>
                  <textarea id="message" name="message" required rows={5} value={form.message} onChange={onChange} data-testid="contact-input-message" className="input-field resize-none" placeholder="Mesajınızı yazın" />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="btn-primary mt-8 w-full disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Gönderiliyor..." : "Gönder"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section data-testid="randevu-section" className="border-t border-line bg-cream-200">
        <div className="container-x section-y">
          <Reveal>
            <RandevuForm />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Iletisim;
